import React, { useMemo } from "react";
import styled from "styled-components";
import { Typography, Space } from "antd";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import _ from "lodash";

import { ReactComponent as HazardTriangle } from "../../assets/svg/hazardTriangle.svg";
import { selectPatientDetails, selectPatientJourney } from "../../store/selector";
import { WARNING_50, WARNING_200, WARNING_500, WARNING_600, GRAY_700 } from "../../constant/colors";
import { MISSING_ELEMENT_ANCHORS } from "../../constant/managePatientDetails";
import NavigateMPDButton from "./PatientPotentialSavings/NavigateMPDButton";

const { Text } = Typography;

const BannerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 45px;
  gap: 8px;
  padding: 8px 16px;
  margin: 0 15px 10px 15px;
  border-radius: 8px;
  border: 1px solid ${WARNING_200};
  background: ${WARNING_50};
`;

const AlertText = styled(Text)`
  color: ${GRAY_700};
  font-size: 14px;
  line-height: 20px;
`;

const StyledHazardTriangle = styled(HazardTriangle)`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 5.5px;
  path {
    stroke: ${WARNING_500};
    stroke-width: 1.5;
  }
`;

const PatientMissingDataBanner = () => {
  const { t } = useTranslation();
  const patientDetails = useSelector(selectPatientDetails);
  const patientJourney = useSelector(selectPatientJourney);

  const { missingElements, firstMissingType } = useMemo(() => {
    if (!patientDetails || !patientJourney) return { missingElements: [], firstMissingType: null };

    const elements = [];
    let firstType = null;
    const { events } = patientJourney;
    const { primaryDiagnosesForDisplay, coverageDetailsFromDatum } = patientDetails;

    // Priority order: Coverage → Diagnosis → Medication
    if (coverageDetailsFromDatum?.length === 0) {
      elements.push("Coverage");
      if (!firstType) firstType = "coverage";
    }

    if (primaryDiagnosesForDisplay?.length === 0) {
      elements.push("Diagnosis");
      if (!firstType) firstType = "diagnosis";
    }

    const unHiddenDrugs = _.flattenDeep(events?.map((event) => event.drugs) || []).filter(
      (drug) => drug?.isHidden === false
    );
    if (unHiddenDrugs?.length === 0) {
      elements.push("Medication");
      if (!firstType) firstType = "drug";
    }

    return { missingElements: elements, firstMissingType: firstType };
  }, [patientDetails, patientJourney]);

  const formatMissingElements = (elementsArray) => {
    return elementsArray.map((element, index, array) => {
      const boldElement = <strong key={element}>{element}</strong>;
      if (index === 0) {
        return boldElement;
      }
      if (index === array.length - 1) {
        const separator = array.length === 2 ? " and " : ", and ";
        return (
          <React.Fragment key={element}>
            {separator}
            {boldElement}
          </React.Fragment>
        );
      }
      return <React.Fragment key={element}>, {boldElement}</React.Fragment>;
    });
  };

  if (missingElements.length === 0) return null;

  return (
    <BannerContainer>
      <Space align="center" style={{ alignItems: "center" }}>
        <StyledHazardTriangle />
        <AlertText>
          {t("patientPotentialSavings.add_more_data_prefix")}
          {formatMissingElements(missingElements)}
          {t("patientPotentialSavings.add_more_data_suffix")}
        </AlertText>
      </Space>
      <NavigateMPDButton
        buttonText={t("patientPotentialSavings.add_details")}
        id="patient_missing_data_banner_add_button"
        scrollToSection={firstMissingType ? MISSING_ELEMENT_ANCHORS[firstMissingType] : null}
        color={WARNING_600}
      />
    </BannerContainer>
  );
};

export default PatientMissingDataBanner;
